import { AlertTriangle, RefreshCcw } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import { ErrorBoundary as ReactErrorBoundary, type FallbackProps } from 'react-error-boundary';

import { isApiError } from '@/entities/api-response/types';
import { getErrorMessage } from '@/shared/api/errors';
import { Button } from '@/shared/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/Card';

interface ErrorBoundaryProps {
  readonly children: ReactNode;
}

function ErrorFallback({ error, resetErrorBoundary }: FallbackProps): ReactElement {
  const message = isApiError(error)
    ? getErrorMessage(error)
    : error instanceof Error
      ? error.message
      : 'Ha ocurrido un error inesperado.';

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <Card className="max-w-md">
        <CardHeader>
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10 text-destructive">
              <AlertTriangle className="h-5 w-5" />
            </span>
            <div>
              <CardTitle>Algo salió mal</CardTitle>
              <CardDescription>Estamos aquí, pero esta vista falló.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="rounded-md bg-muted p-3 font-mono text-xs text-muted-foreground">
            {message}
          </p>
          <div className="flex gap-2">
            <Button onClick={resetErrorBoundary} leftIcon={<RefreshCcw />}>
              Reintentar
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                window.location.assign('/');
              }}
            >
              Ir al inicio
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function ErrorBoundary({ children }: ErrorBoundaryProps): ReactElement {
  return <ReactErrorBoundary FallbackComponent={ErrorFallback}>{children}</ReactErrorBoundary>;
}
